import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, pillClass, statusLabel, fmtDate, timeAgo, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { WhatsappShareButton } from "../components/WhatsappShareButton";
import { toast } from "sonner";
import { Sparkles, User, ChevronDown } from "lucide-react";

export default function IssueDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [members, setMembers] = useState([]);

  const load = useCallback(async () => {
    const r = await api.get(`/issues/${id}`);
    setData(r.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user?.community_id) {
      api.get(`/communities/${user.community_id}/users`)
        .then((u) => setMembers(u.data))
        .catch((e) => console.error("members load failed", e));
    }
  }, [user?.community_id]);

  const activityFeed = useMemo(() => {
    const timeline = data?.timeline || [];
    const comments = data?.comments || [];
    return [
      ...timeline.map((t) => ({ kind: "timeline", ts: t.created_at, t })),
      ...comments.map((c) => ({ kind: "comment", ts: c.created_at, c })),
    ].sort((a, b) => (a.ts < b.ts ? -1 : 1));
  }, [data]);

  async function patch(payload, successMsg) {
    try { await api.patch(`/issues/${id}`, payload); load(); toast.success(successMsg); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function postComment(e) {
    e.preventDefault();
    if (!comment.trim()) return;
    try { await api.post(`/issues/${id}/comments`, { text: comment }); setComment(""); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  if (!data) return <div className="p-10 text-stone-500">Loading…</div>;
  const { issue, timeline, comments } = data;
  const canManage = ["committee", "staff"].includes(user?.role) || user?.id === issue.created_by;
  const photoAttachments = (issue.attachments || []).filter((a) => (a.content_type || "").startsWith("image/"));

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl pb-32">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs uppercase tracking-wider text-stone-500" data-testid="issue-number">#{issue.number || "—"}</span>
        <span className="text-xs uppercase tracking-widest text-stone-500 font-bold capitalize">{issue.category}</span>
      </div>
      <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1" data-testid="issue-title">{issue.title}</h1>

      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="issue-meta">
        {canManage ? (
          <>
            <select value={issue.status} onChange={(e) => patch({ status: e.target.value }, `Status → ${statusLabel(e.target.value)}`)}
              data-testid="status-select"
              className={`${pillClass(issue.status)} appearance-none pr-6 cursor-pointer border-0 outline-none`}>
              {["new","acknowledged","assigned","in_progress","resolved","closed","reopened"].map((s) =>
                <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
            <select value={issue.priority} onChange={(e) => patch({ priority: e.target.value }, `Priority → ${e.target.value}`)}
              data-testid="priority-select"
              className={`${pillClass(issue.priority)} appearance-none pr-6 cursor-pointer border-0 outline-none capitalize`}>
              {["low","medium","high","urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </>
        ) : (
          <>
            <span className={pillClass(issue.status)}>{statusLabel(issue.status)}</span>
            <span className={pillClass(issue.priority)}>{issue.priority}</span>
          </>
        )}
        <span className="text-xs text-stone-500 inline-flex items-center gap-1">
          <User size={12} /> Raised by <strong className="text-stone-700 font-medium">{issue.creator_name || "Unknown"}</strong> · {timeAgo(issue.created_at)}
        </span>
        <WhatsappShareButton entityType="issue" entityId={issue.id}
          fallbackText={`*Issue #${issue.number}* — ${issue.title}\nStatus: ${statusLabel(issue.status)}\n— via NivasOne`}
          className="ml-auto h-9 px-4 text-xs" testid="whatsapp-share" label="Share" />
      </div>

      {canManage && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="text-stone-500">Assign to:</span>
          <select value={issue.assigned_to || ""}
            onChange={(e) => patch({ assigned_to: e.target.value || null }, "Assigned")}
            data-testid="assignee-select"
            className="rounded-full border border-stone-300 px-3 py-1 bg-white">
            <option value="">— Unassigned —</option>
            {members.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
      )}

      {issue.ai_summary && (
        <div className="mt-5 bg-[#FDFBF7] border border-[#E8D8D3] rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#C05640] font-bold inline-flex items-center gap-1.5"><Sparkles size={12} /> AI summary</div>
          <p className="text-sm text-stone-800 mt-1.5 leading-relaxed">{issue.ai_summary}</p>
          {issue.ai_sentiment && <span className="mt-2 inline-block text-[10px] uppercase tracking-wider text-stone-500">Tone: {issue.ai_sentiment}</span>}
        </div>
      )}

      {issue.description && (
        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-stone-500">Description</div>
          <p className="text-stone-800 leading-relaxed mt-1.5 whitespace-pre-wrap">{issue.description}</p>
        </div>
      )}

      {photoAttachments.length > 0 && (
        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Photos ({photoAttachments.length})</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="issue-photos">
            {photoAttachments.map((p) => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.storage_path}`;
              return (
                <a key={p.id} href={url} target="_blank" rel="noreferrer"
                   className="block aspect-square rounded-xl overflow-hidden border border-stone-200 bg-stone-100">
                  <img src={url} alt={p.original_filename} className="w-full h-full object-cover" crossOrigin="use-credentials" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {issue.audio_transcription && (
        <div className="mt-5 bg-stone-50 rounded-xl p-4 italic text-sm text-stone-700">"{issue.audio_transcription}"</div>
      )}

      <div className="mt-7">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold uppercase tracking-wider text-stone-500">Activity</div>
          {activityFeed.length > 0 && (
            <span data-testid="latest-update-pill"
              className="text-[10px] uppercase tracking-wider font-bold text-[#C05640] bg-[#FDFBF7] border border-[#E8D8D3] px-2 py-0.5 rounded-full">
              Latest update · {timeAgo(activityFeed[activityFeed.length - 1].ts)}
            </span>
          )}
        </div>
        <ol className="space-y-3">
          {activityFeed.map((entry) => <ActivityEntry key={entry.kind === "timeline" ? entry.t.id : entry.c.id} entry={entry} />)}
        </ol>
      </div>

      <form onSubmit={postComment} className="mt-6 flex gap-2 items-start" data-testid="comment-form">
        <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment, update or proof note…" data-testid="comment-input" />
        <Button type="submit" className="rounded-full bg-stone-900 text-white">Post</Button>
      </form>
    </div>
  );
}

function ActivityEntry({ entry }) {
  if (entry.kind === "timeline") {
    return (
      <li className="border-l-2 border-stone-200 pl-4 py-1 relative" data-testid={`tl-${entry.t.id}`}>
        <span className="absolute w-2.5 h-2.5 bg-[#C05640] rounded-full -left-[6px] top-2.5" />
        <div className="text-xs text-stone-500">{entry.t.by_user_name || "Someone"} · {timeAgo(entry.t.created_at)}</div>
        <div className="text-sm text-stone-800">{entry.t.summary || entry.t.event?.replaceAll("_", " ")}</div>
      </li>
    );
  }
  return (
    <li className="border-l-2 border-stone-200 pl-4 py-1 relative">
      <span className="absolute w-2.5 h-2.5 bg-stone-400 rounded-full -left-[6px] top-2.5" />
      <div className="text-xs text-stone-500">{entry.c.user_name} · {timeAgo(entry.c.created_at)}</div>
      <div className="text-sm text-stone-800">{entry.c.text}</div>
    </li>
  );
}
